/*
 * Top-level router for the AquaStar admin console.
 *
 * Public routes:
 *   /login               — Login form (two-pane, AquaStar palette)
 *   /forgot-password     — (Coming soon — placeholder)
 *
 * Gated routes (require staff role on aquastar_staff):
 *   /                    — redirects to /dashboard
 *   /dashboard           — KPI tiles + activity feed
 *   /users               — Users list (aquastarListUsers)
 *   /pumps               — Pumps list (aquastarListAllPumps)
 *   /pumps/:deviceId     — Pump detail (read-only first pass)
 *   /service             — Service queue (Coming soon)
 *   /settings            — Console settings (Coming soon)
 *   /audit               — Audit log (Coming soon)
 *
 * The route guard reads from useAuth():
 *   - 'unknown' → show splash (don't flash login page on refresh)
 *   - 'guest'   → redirect to /login, preserving the intended path
 *   - 'denied'  → render "not authorized" page (signed in, not staff)
 *   - 'authed'  → render the requested route inside <AdminLayout>
 */

import { lazy, Suspense, type ReactElement } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { NexusAdminProvider, NexusAdminRoutes, WikiPage } from '@nexus/admin-ui';
import FloatingAssistant from './components/FloatingAssistant';
import { AuthProvider, useAuth } from './lib/auth';
import { getToken } from './lib/apiClient';
import { GATEWAY_BASE } from './lib/config';

// Lazy-load page chunks so the login bundle stays small.
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
// Users + UserDetail lazy imports removed 2026-05-21 — the /users
// route now redirects to /platform/users. The page components stay
// in src/pages/ for Wave 2a/2b/2c to mine the existing UI patterns
// (staff grant/revoke modal, pump-access panel, invite modal) when
// migrating to dedicated pages; once Wave 3 deletes those, the files
// can be removed too. Eslint will flag the orphaned files; that's
// expected during the transition.
const Pumps = lazy(() => import('./pages/Pumps'));
const UsersSupport = lazy(() => import('./pages/UsersSupport'));
const Issues = lazy(() => import('./pages/Issues'));
const Firmware = lazy(() => import('./pages/Firmware'));
const PumpDetail = lazy(() => import('./pages/PumpDetail'));
const PumpControl = lazy(() => import('./pages/PumpControl'));
const NotAuthorized = lazy(() => import('./pages/NotAuthorized'));
const ComingSoon = lazy(() => import('./pages/ComingSoon'));
const Settings = lazy(() => import('./pages/Settings'));
const AccessTokens = lazy(() => import('./pages/AccessTokens'));
const AdminLayout = lazy(() => import('./components/AdminLayout'));

function Splash() {
  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{ height: '100vh' }}
    >
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading…</span>
      </div>
    </div>
  );
}

/**
 * Wrapper that defers rendering until we know whether the user is
 * signed in. Without this, a refresh on /dashboard flashes the
 * login screen for ~200ms while staffMe is in flight.
 */
function RequireStaff({ children }: { children: ReactElement }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'unknown') return <Splash />;
  if (status === 'guest') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (status === 'denied') return <NotAuthorized />;
  return children;
}

/**
 * Vendor scope (AQUASTAR-336). A `vendor` account (Emaux) is admitted by
 * aquastarStaffMe so the shell renders, but it must only ever see the
 * Firmware page: every other gated route bounces it to /firmware, and the
 * Sidebar hides the rest of the nav. Server-side, every non-firmware
 * handler already 403s a vendor — this is UX, not the security boundary.
 */
function HomeRedirect() {
  const { staff } = useAuth();
  return <Navigate to={staff?.role === 'vendor' ? '/firmware' : '/dashboard'} replace />;
}

function VendorScope({ children }: { children: ReactElement }) {
  const { staff } = useAuth();
  if (staff?.role === 'vendor') return <Navigate to="/firmware" replace />;
  return children;
}

/**
 * Like RequireStaff, but additionally requires users.is_platform_admin.
 * Guards the /platform/* subtree so a non-platform-admin staffer who types
 * the URL directly is bounced back to the AquaStar console — matching the
 * Sidebar, which hides the Platform nav link for the same group. The Nexus
 * platform endpoints are also platform-admin gated server-side (defense in
 * depth); this just avoids rendering a shell of 403ing pages.
 */
function RequirePlatformAdmin({ children }: { children: ReactElement }) {
  const { status, staff } = useAuth();
  const location = useLocation();

  if (status === 'unknown') return <Splash />;
  if (status === 'guest') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (status === 'denied') return <NotAuthorized />;
  if (staff?.isPlatformAdmin !== true) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

export default function App() {
  // NexusAdminProvider sits outside AuthProvider so the platform-admin
  // pages can read the gateway base + bearer token without depending on
  // the AquaStar-specific auth context. The token is read fresh on
  // every nexusFetch call via getToken() (sessionStorage), so rotation
  // by AquaStar's auth flow is picked up transparently.
  return (
    <NexusAdminProvider apiBase={GATEWAY_BASE} getAuthToken={getToken}>
      <AuthProvider>
        <Suspense fallback={<Splash />}>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<Login />} />
            <Route
              path="/forgot-password"
              element={
                <ComingSoon title="Forgot password" body="Self-service password reset is coming soon. For now, please contact an existing admin." />
              }
            />

            {/* Gated — wrapped in AdminLayout (sidebar + topbar). */}
            <Route
              element={
                <RequireStaff>
                  <AdminLayout />
                </RequireStaff>
              }
            >
              <Route index element={<HomeRedirect />} />
              <Route path="/dashboard" element={<VendorScope><Dashboard /></VendorScope>} />
              {/* Firmware releases (AQUASTAR-336) — the one page a vendor
                  (Emaux) can use; staff read, admin manages. */}
              <Route path="/firmware" element={<Firmware />} />
              {/*
                /users — re-activated 2026-07-08 (AQUASTAR-115) as the
                app-tier customer-support Users page (UsersSupport.tsx:
                collapsible per-user cards w/ pumps; Disable / Send
                password reset / Generate recovery code — client users
                only). Identity management (staff, invites, platform
                admin) stays on /platform/users. The pre-2026-05-21
                Users.tsx + UserDetail.tsx remain orphaned pattern
                references until Wave 3 deletes them.
              */}
              <Route path="/users" element={<VendorScope><UsersSupport /></VendorScope>} />
              <Route
                path="/users/:userId"
                element={<Navigate to="/users" replace />}
              />
              <Route path="/pumps" element={<VendorScope><Pumps /></VendorScope>} />
              <Route path="/issues" element={<VendorScope><Issues /></VendorScope>} />
              <Route path="/issues/:issueId" element={<VendorScope><Issues /></VendorScope>} />
              <Route path="/pumps/:deviceId" element={<VendorScope><PumpDetail /></VendorScope>} />
              <Route
                path="/pumps/:deviceId/control"
                element={<VendorScope><PumpControl /></VendorScope>}
              />
              <Route
                path="/service"
                element={<VendorScope><ComingSoon title="Service queue" body="Field-tech assignment + visit log." /></VendorScope>}
              />
              <Route path="/settings" element={<VendorScope><Settings /></VendorScope>} />
              <Route path="/access-tokens" element={<AccessTokens />} />
              {/*
                Wiki (NEXUS-119) — the shared @nexus/admin-ui page, locked to
                the `aquastar` space: URLs are /wiki/<slug> with no app segment
                and no space switcher. Pages come from the five AquaStar repos'
                _wiki.yml manifests, synced by the dev instance; Architecture
                and Developers sync on dev only, Platform setup everywhere.
                Staff only (VendorScope) — a vendor sees Firmware alone.
              */}
              <Route path="/wiki/*" element={<VendorScope><WikiPage fixedApp="aquastar" /></VendorScope>} />
              <Route
                path="/audit"
                element={<VendorScope><ComingSoon title="Audit log" body="Read-only feed of admin actions." /></VendorScope>}
              />
            </Route>

            {/*
             * Nexus platform-admin pages — mounted at /platform/* and gated by
             * RequirePlatformAdmin so only platform admins (users.is_platform_admin)
             * reach them, matching the Sidebar which hides the Platform nav link
             * for non-admins. NexusAdminRoutes brings its own AdminLayout
             * (Nexus-branded sidebar) inside the wildcard subtree.
             */}
            <Route
              path="/platform/*"
              element={
                <RequirePlatformAdmin>
                  <>
                  <NexusAdminRoutes
                    basePath="/platform"
                    backHref="/dashboard"
                    backLabel="Back to AquaStar Admin"
                  />
                  {/* NEXUS-1384: the assistant on the platform pages too (their layout has no topbar slot). */}
                  <FloatingAssistant />
                  </>
                </RequirePlatformAdmin>
              }
            />

            {/* Catch-all */}
            <Route path="*" element={<HomeRedirect />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </NexusAdminProvider>
  );
}
